import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    Building2,
    Globe,
    Instagram,
    Youtube,
    Twitter,
    Facebook,
    Linkedin,
    Mail,
    Phone,
    Bell,
    ShieldCheck,
    Save,
    Loader2,
    Info,
    FileText,
    Users,
    Lock,
    Check,
    ChevronDown,
    Calendar,
    Palmtree,
    IndianRupee,
    Receipt,
    Award,
    LayoutDashboard,
    BarChart3,
    ShoppingCart,
    Sparkles,
    MessageSquare,
    Building2 as BranchIcon,
    CreditCard,
} from 'lucide-react';
import { useApi } from '../utils/useApi';
import { useUserContext } from '../contexts/UserContext';
import toast from 'react-hot-toast';
import { WhatsappSettings } from '../components/notifications/WhatsappSettings';
import NumberInput from '../components/ui/NumberInput';
import GstinAndAddressSection from '../components/settings/GstinAndAddressSection';
import PaymentSettingsSection, { PaymentSettings } from '../components/settings/PaymentSettingsSection';

interface GymSettings {
    id: string;
    name: string;
    displayName: string;
    email: string;
    phoneNumbers: string[];
    gstin: string | null;
    address: string | null;
    registeredAddress: string | null;
    notificationName: string | null;
    website: string | null;
    logoUrl: string | null;
    instagramHandle: string | null;
    youtubeHandle: string | null;
    twitterHandle: string | null;
    facebookHandle: string | null;
    linkedinHandle: string | null;
    gymCode: string | null;
    termsAndConditions: string | null;
    privacyPolicy: string | null;
    trainerIncentiveThreshold: number | null;
}

// ─── HRMS Permissions Section ────────────────────────────────────────────────

const HRMS_TAB_DEFS = [
    { id: 'attendance', label: 'Attendance', description: 'View and manage staff attendance logs', icon: Calendar },
    { id: 'leaves', label: 'Leaves', description: 'View and manage staff leave requests', icon: Palmtree },
    { id: 'salary', label: 'Salary', description: 'View and manage staff salary information', icon: IndianRupee },
    { id: 'payslips', label: 'Payslips', description: 'View and generate staff payslips', icon: Receipt },
] as const;

type HrmsTabId = typeof HRMS_TAB_DEFS[number]['id'];

interface AdminHrmsData {
    userId: string;
    name: string;
    displayName: string | null;
    email: string | null;
    phoneNo: string | null;
    permissions: Record<HrmsTabId, boolean>;
}

function HrmsPermissionsSection({ gymId }: { gymId: string }) {
    const { fetchWithAuth } = useApi();
    const [admins, setAdmins] = useState<AdminHrmsData[]>([]);
    const [selectedAdminId, setSelectedAdminId] = useState<string>('');
    const [localPermissions, setLocalPermissions] = useState<Record<HrmsTabId, boolean>>({
        attendance: false, leaves: false, salary: false, payslips: false,
    });
    const [isFetching, setIsFetching] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isDirty, setIsDirty] = useState(false);

    useEffect(() => {
        if (!gymId) return;
        setIsFetching(true);
        fetchWithAuth(`/api/hrms-permissions/admins?gymId=${gymId}`)
            .then((res) => {
                if (res.success) setAdmins(res.data);
            })
            .catch(() => toast.error('Failed to load admin list'))
            .finally(() => setIsFetching(false));
    }, [gymId]);

    const selectedAdmin = admins.find(a => a.userId === selectedAdminId);

    useEffect(() => {
        if (selectedAdmin) {
            setLocalPermissions({ ...selectedAdmin.permissions });
            setIsDirty(false);
        }
    }, [selectedAdminId]);

    const handleToggle = (tab: HrmsTabId) => {
        setLocalPermissions(prev => ({ ...prev, [tab]: !prev[tab] }));
        setIsDirty(true);
    };

    const handleSave = async () => {
        if (!selectedAdminId) return;
        setIsSaving(true);
        try {
            const res = await fetchWithAuth(`/api/hrms-permissions/${selectedAdminId}?gymId=${gymId}`, {
                method: 'PUT',
                body: JSON.stringify({ permissions: localPermissions }),
            });
            if (res.success) {
                toast.success('Permissions saved');
                setAdmins(prev => prev.map(a =>
                    a.userId === selectedAdminId ? { ...a, permissions: { ...localPermissions } } : a
                ));
                setIsDirty(false);
            } else {
                toast.error(res.error || 'Failed to save permissions');
            }
        } catch {
            toast.error('Failed to save permissions');
        } finally {
            setIsSaving(false);
        }
    };

    if (isFetching) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
        );
    }

    return (
        <div className="p-4 sm:p-8 space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
            <div>
                <h2 className="text-xl font-bold text-gray-900">HRMS Permissions</h2>
                <p className="text-sm text-gray-500 mt-1">
                    Control which HRMS tabs each admin can access. Owners always have full access.
                </p>
            </div>

            {admins.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 gap-4 text-center">
                    <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center">
                        <Users className="h-7 w-7 text-gray-400" />
                    </div>
                    <div>
                        <p className="font-semibold text-gray-700">No admins yet</p>
                        <p className="text-sm text-gray-500 mt-1">
                            Add admins to your gym from the Team page to manage their HRMS access.
                        </p>
                    </div>
                </div>
            ) : (
                <div className="space-y-6">
                    {/* Admin selector */}
                    <div className="space-y-2">
                        <label className="text-sm font-semibold text-gray-700">Select Admin</label>
                        <div className="relative">
                            <select
                                value={selectedAdminId}
                                onChange={(e) => setSelectedAdminId(e.target.value)}
                                className="w-full appearance-none px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all outline-none text-sm text-gray-700 pr-10"
                            >
                                <option value="">— Choose an admin —</option>
                                {admins.map(admin => (
                                    <option key={admin.userId} value={admin.userId}>
                                        {admin.name}{admin.email ? ` (${admin.email})` : ''}
                                    </option>
                                ))}
                            </select>
                            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                        </div>
                    </div>

                    {/* Permissions list */}
                    {selectedAdminId && (
                        <div className="space-y-3">
                            <p className="text-sm font-semibold text-gray-700">HRMS Tab Access</p>
                            <div className="divide-y divide-gray-100 border border-gray-200 rounded-xl overflow-hidden">
                                {HRMS_TAB_DEFS.map(tab => {
                                    const Icon = tab.icon;
                                    const enabled = localPermissions[tab.id];
                                    return (
                                        <label
                                            key={tab.id}
                                            className="flex items-center gap-4 px-5 py-4 cursor-pointer hover:bg-gray-50 transition-colors"
                                        >
                                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${enabled ? 'bg-blue-100' : 'bg-gray-100'}`}>
                                                <Icon className={`h-5 w-5 ${enabled ? 'text-blue-600' : 'text-gray-400'}`} />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-semibold text-gray-900">{tab.label}</p>
                                                <p className="text-xs text-gray-500 mt-0.5">{tab.description}</p>
                                            </div>
                                            <div className="flex-shrink-0">
                                                <div
                                                    className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer ${enabled ? 'bg-blue-600' : 'bg-gray-200'}`}
                                                    onClick={() => handleToggle(tab.id)}
                                                >
                                                    <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${enabled ? 'translate-x-5' : 'translate-x-0'}`} />
                                                </div>
                                            </div>
                                        </label>
                                    );
                                })}
                            </div>
                            <p className="text-xs text-gray-400">
                                Changes take effect the next time the admin logs in or refreshes the page.
            </p>

                            {/* Save button */}
                            <div className="flex justify-end pt-2">
                                <button
                                    type="button"
                                    disabled={!isDirty || isSaving}
                                    onClick={handleSave}
                                    className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 transition-all shadow-lg shadow-blue-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                                >
                                    {isSaving ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                        <Check className="h-4 w-4" />
                                    )}
                                    Save Permissions
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// ─── Team Permissions Section ────────────────────────────────────────────────

const RESOURCE_DEFS = [
    { id: 'dashboard', label: 'Dashboard', description: 'View the gym dashboard and stats', icon: LayoutDashboard },
    { id: 'reports', label: 'Reports', description: 'View and export reports', icon: BarChart3 },
    { id: 'purchases', label: 'Purchases', description: 'View and manage purchases & invoices', icon: ShoppingCart },
    { id: 'expenses', label: 'Expenses', description: 'View and manage expenses', icon: IndianRupee },
    { id: 'attendance', label: 'Attendance', description: 'View and manage member attendance', icon: Calendar },
    { id: 'notifications', label: 'Notifications', description: 'View and send notifications/campaigns', icon: MessageSquare },
    { id: 'ai', label: 'Reach AI', description: 'Access the Reach AI follow-up tools', icon: Sparkles },
] as const;

type ResourceId = typeof RESOURCE_DEFS[number]['id'];
type ResourcePermMap = Record<ResourceId, { view: boolean; edit: boolean }>;

interface TeamMemberPermData {
    userId: string;
    role: string;
    name: string;
    displayName: string | null;
    email: string | null;
    phoneNo: string | null;
    branchIds: string[];
    permissions: ResourcePermMap;
}

interface BranchLite {
    id: string;
    name: string;
    displayName: string | null;
}

function emptyPerms(): ResourcePermMap {
    return RESOURCE_DEFS.reduce((acc, r) => {
        acc[r.id] = { view: false, edit: false };
        return acc;
    }, {} as ResourcePermMap);
}

function TeamPermissionsSection({ gymId }: { gymId: string }) {
    const { fetchWithAuth } = useApi();
    const [members, setMembers] = useState<TeamMemberPermData[]>([]);
    const [branches, setBranches] = useState<BranchLite[]>([]);
    const [selectedUserId, setSelectedUserId] = useState<string>('');
    const [localPerms, setLocalPerms] = useState<ResourcePermMap>(emptyPerms());
    const [localBranchIds, setLocalBranchIds] = useState<string[]>([]);
    const [isFetching, setIsFetching] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isDirty, setIsDirty] = useState(false);

    useEffect(() => {
        if (!gymId) return;
        setIsFetching(true);
        Promise.all([
            fetchWithAuth(`/api/team-permissions/members?gymId=${gymId}`),
            fetchWithAuth('/api/branches'),
        ])
            .then(([permRes, branchRes]) => {
                if (permRes.success) setMembers(permRes.data);
                const list: BranchLite[] = (branchRes.data || branchRes.branches || []).filter(
                    (b: any) => (b.status ?? 'active') !== 'inactive'
                );
                setBranches(list);
            })
            .catch(() => toast.error('Failed to load team permissions'))
            .finally(() => setIsFetching(false));
    }, [gymId]);

    const selectedMember = members.find(m => m.userId === selectedUserId);

    useEffect(() => {
        if (selectedMember) {
            setLocalPerms({ ...emptyPerms(), ...selectedMember.permissions });
            setLocalBranchIds([...selectedMember.branchIds]);
            setIsDirty(false);
        }
    }, [selectedUserId]);

    const togglePerm = (resource: ResourceId, mode: 'view' | 'edit') => {
        setLocalPerms(prev => {
            const cur = prev[resource];
            let next = { ...cur, [mode]: !cur[mode] };
            // Keep view/edit consistent: edit needs view; clearing view clears edit.
            if (mode === 'edit' && next.edit) next.view = true;
            if (mode === 'view' && !next.view) next.edit = false;
            return { ...prev, [resource]: next };
        });
        setIsDirty(true);
    };

    const toggleBranch = (branchId: string) => {
        setLocalBranchIds(prev =>
            prev.includes(branchId) ? prev.filter(id => id !== branchId) : [...prev, branchId]
        );
        setIsDirty(true);
    };

    const handleSave = async () => {
        if (!selectedUserId) return;
        setIsSaving(true);
        try {
            const res = await fetchWithAuth(`/api/team-permissions/${selectedUserId}?gymId=${gymId}`, {
                method: 'PUT',
                body: JSON.stringify({ permissions: localPerms, branchIds: localBranchIds }),
            });
            if (res.success) {
                toast.success('Team permissions saved');
                setMembers(prev => prev.map(m =>
                    m.userId === selectedUserId
                        ? { ...m, permissions: { ...localPerms }, branchIds: [...localBranchIds] }
                        : m
                ));
                setIsDirty(false);
            } else {
                toast.error(res.error || 'Failed to save permissions');
            }
        } catch {
            toast.error('Failed to save permissions');
        } finally {
            setIsSaving(false);
        }
    };

    if (isFetching) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
        );
    }

    return (
        <div className="p-4 sm:p-8 space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
            <div>
                <h2 className="text-xl font-bold text-gray-900">Team Permissions</h2>
                <p className="text-sm text-gray-500 mt-1">
                    Give each team member access to specific branches and features. By default a
                    team member has no access until you grant it here. Owners always have full access.
                </p>
            </div>

            {members.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 gap-4 text-center">
                    <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center">
                        <Users className="h-7 w-7 text-gray-400" />
                    </div>
                    <div>
                        <p className="font-semibold text-gray-700">No team members yet</p>
                        <p className="text-sm text-gray-500 mt-1">
                            Add team members from the Team page to manage their access here.
                        </p>
                    </div>
                </div>
            ) : (
                <div className="space-y-6">
                    {/* Member selector */}
                    <div className="space-y-2">
                        <label className="text-sm font-semibold text-gray-700">Select Team Member</label>
                        <div className="relative">
                            <select
                                value={selectedUserId}
                                onChange={(e) => setSelectedUserId(e.target.value)}
                                className="w-full appearance-none px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all outline-none text-sm text-gray-700 pr-10"
                            >
                                <option value="">— Choose a team member —</option>
                                {members.map(m => (
                                    <option key={m.userId} value={m.userId}>
                                        {m.name}{m.role ? ` · ${m.role}` : ''}{m.email ? ` (${m.email})` : ''}
                                    </option>
                                ))}
                            </select>
                            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                        </div>
                    </div>

                    {selectedUserId && (
                        <>
                            {/* Branch access */}
                            <div className="space-y-3">
                                <div className="flex items-center gap-2">
                                    <BranchIcon className="h-4 w-4 text-gray-500" />
                                    <p className="text-sm font-semibold text-gray-700">Branch Access</p>
                                </div>
                                {branches.length === 0 ? (
                                    <p className="text-xs text-gray-500">No branches configured for this gym.</p>
                                ) : (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                        {branches.map(b => {
                                            const checked = localBranchIds.includes(b.id);
                                            return (
                                                <label
                                                    key={b.id}
                                                    className={`flex items-center gap-3 px-4 py-3 rounded-xl border cursor-pointer transition-colors ${checked ? 'border-blue-200 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={checked}
                                                        onChange={() => toggleBranch(b.id)}
                                                        className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                                                    />
                                                    <span className="text-sm font-medium text-gray-800 truncate">{b.displayName || b.name}</span>
                                                </label>
                                            );
                                        })}
                                    </div>
                                )}
                                <p className="text-xs text-gray-400">
                                    The member only sees data for the branches selected here. Leave all
                                    unchecked to block access to every branch.
                                </p>
                            </div>

                            {/* Feature permissions */}
                            <div className="space-y-3">
                                <p className="text-sm font-semibold text-gray-700">Feature Access</p>
                                <div className="border border-gray-200 rounded-xl overflow-hidden">
                                    <div className="hidden sm:flex items-center px-5 py-2 bg-gray-50 text-[11px] font-bold uppercase tracking-wider text-gray-400">
                                        <span className="flex-1">Feature</span>
                                        <span className="w-16 text-center">View</span>
                                        <span className="w-16 text-center">Edit</span>
                                    </div>
                                    <div className="divide-y divide-gray-100">
                                        {RESOURCE_DEFS.map(r => {
                                            const Icon = r.icon;
                                            const perm = localPerms[r.id];
                                            return (
                                                <div key={r.id} className="flex items-center gap-4 px-5 py-4">
                                                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${perm.view ? 'bg-blue-100' : 'bg-gray-100'}`}>
                                                        <Icon className={`h-5 w-5 ${perm.view ? 'text-blue-600' : 'text-gray-400'}`} />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-semibold text-gray-900">{r.label}</p>
                                                        <p className="text-xs text-gray-500 mt-0.5">{r.description}</p>
                                                    </div>
                                                    <div className="w-16 flex justify-center">
                                                        <div
                                                            className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer ${perm.view ? 'bg-blue-600' : 'bg-gray-200'}`}
                                                            onClick={() => togglePerm(r.id, 'view')}
                                                            title="View"
                                                        >
                                                            <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${perm.view ? 'translate-x-5' : 'translate-x-0'}`} />
                                                        </div>
                                                    </div>
                                                    <div className="w-16 flex justify-center">
                                                        <div
                                                            className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer ${perm.edit ? 'bg-emerald-600' : 'bg-gray-200'}`}
                                                            onClick={() => togglePerm(r.id, 'edit')}
                                                            title="Edit"
                                                        >
                                                            <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${perm.edit ? 'translate-x-5' : 'translate-x-0'}`} />
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                                <p className="text-xs text-gray-400">
                                    Changes take effect the next time the member logs in or refreshes.
                                </p>
                            </div>

                            {/* Save */}
                            <div className="flex justify-end pt-2">
                                <button
                                    type="button"
                                    disabled={!isDirty || isSaving}
                                    onClick={handleSave}
                                    className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 transition-all shadow-lg shadow-blue-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                                >
                                    {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                                    Save Permissions
                                </button>
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

// ─── Main Settings Page ───────────────────────────────────────────────────────

export default function GymSettingsPage() {
    const { fetchWithAuth } = useApi();
    const queryClient = useQueryClient();
    const [activeSection, setActiveSection] = useState(() => {
        const hash = window.location.hash.replace('#', '');
        return ['general', 'business', 'policy', 'notifications', 'social', 'permissions', 'team-permissions'].includes(hash) ? hash : 'general';
    });
    const [formData, setFormData] = useState<Partial<GymSettings>>({});
    const [phoneErrors, setPhoneErrors] = useState<Record<number, string>>({});

    // Update URL hash when tab changes
    useEffect(() => {
        window.location.hash = activeSection;
    }, [activeSection]);

    // Handle back/forward buttons for hash changes
    useEffect(() => {
        const handleHashChange = () => {
            const hash = window.location.hash.replace('#', '');
            if (hash && ['general', 'business', 'policy', 'notifications', 'social', 'permissions', 'team-permissions'].includes(hash)) {
                setActiveSection(hash);
            }
        };
        window.addEventListener('hashchange', handleHashChange);
        return () => window.removeEventListener('hashchange', handleHashChange);
    }, []);

    // Indian phone validation
    const isIndianPhoneNumber = (phone: string): boolean => {
        if (!phone || phone.trim() === '') return true; // Empty is handled during filter
        const phoneRegex = /^[6-9]\d{9}$/;
        return phoneRegex.test(phone.replace(/\D/g, ''));
    };

    const { userContext } = useUserContext();
    const gymId = userContext?.accessibleGyms?.[0];

    // Fetch gym details
    const { data: gymSettingsData, isLoading } = useQuery({
        queryKey: ['gym-settings', gymId],
        queryFn: async () => {
            const response = await fetchWithAuth(`/api/gyms/${gymId}`);
            return response.data as GymSettings;
        },
        enabled: !!gymId,
    });

    // Online payments — the section appears only once Gympe has enabled them
    // for this gym, so a gym that hasn't opted in sees no change.
    const { data: paymentSettings, refetch: refetchPaymentSettings } = useQuery({
        queryKey: ['payment-settings', gymId],
        queryFn: async () => {
            const response = await fetchWithAuth(`/api/commerce/payment-settings?gymId=${gymId}`);
            return response.data as PaymentSettings;
        },
        enabled: !!gymId,
        retry: false,
    });

    // Store original data to track changes
    const [originalData, setOriginalData] = useState<Partial<GymSettings>>({});

    // Update form data when gym settings are loaded
    useEffect(() => {
        if (gymSettingsData) {
            setFormData(gymSettingsData);
            setOriginalData(gymSettingsData);
        }
    }, [gymSettingsData]);

    // Check if form has changes - check all fields across all tabs
    const hasChanges = () => {
        if (!gymSettingsData || !originalData || Object.keys(originalData).length === 0) return false;

        // All fields that can be changed in the form
        const fieldsToCompare: (keyof GymSettings)[] = [
            'name', 'displayName', 'email', 'phoneNumbers',
            'notificationName', 'website', 'logoUrl', 'instagramHandle', 'youtubeHandle',
            'twitterHandle', 'facebookHandle', 'linkedinHandle', 'gymCode',
            'termsAndConditions', 'privacyPolicy'
        ];

        for (const field of fieldsToCompare) {
            const currentValue = formData[field];
            const originalValue = originalData[field];

            // Handle array comparison (phoneNumbers)
            if (field === 'phoneNumbers') {
                const currentPhones = (currentValue as string[] || []).filter(p => p && p.trim()).sort();
                const originalPhones = (originalValue as string[] || []).filter(p => p && p.trim()).sort();
                if (currentPhones.length !== originalPhones.length) {
                    return true;
                }
                for (let i = 0; i < currentPhones.length; i++) {
                    if (currentPhones[i] !== originalPhones[i]) {
                        return true;
                    }
                }
            } else {
                // Handle null/undefined/empty string comparison - normalize both to empty string if null/undefined
                const current = currentValue == null ? '' : String(currentValue).trim();
                const original = originalValue == null ? '' : String(originalValue).trim();
                if (current !== original) {
                    return true;
                }
            }
        }

        return false;
    };

    // Update gym settings mutation
    const updateMutation = useMutation({
        mutationFn: async (newData: Partial<GymSettings>) => {
            return await fetchWithAuth(`/api/gyms/${gymId}`, {
                method: 'PATCH',
                body: JSON.stringify(newData)
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries(['gym-settings', gymId]);
            toast.success('Settings updated successfully');
            // Reset original data to current form data after successful save
            setOriginalData({ ...formData });
        },
        onError: (error: any) => {
            const errorMessage = error.message || 'Failed to update settings';
            toast.error(errorMessage);
        }
    });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handlePhoneChange = (index: number, value: string) => {
        const newPhones = [...(formData.phoneNumbers || [])];
        newPhones[index] = value;
        setFormData(prev => ({ ...prev, phoneNumbers: newPhones }));

        // Validate phone number
        if (value && !isIndianPhoneNumber(value)) {
            setPhoneErrors(prev => ({ ...prev, [index]: 'Invalid phone number. Please enter a 10-digit number.' }));
        } else {
            setPhoneErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[index];
                return newErrors;
            });
        }
    };

    const addPhone = () => {
        setFormData(prev => ({ ...prev, phoneNumbers: [...(prev.phoneNumbers || []), ''] }));
    };

    const removePhone = (index: number) => {
        const newPhones = (formData.phoneNumbers || []).filter((_, i) => i !== index);
        setFormData(prev => ({ ...prev, phoneNumbers: newPhones }));
        // Also remove error if exists
        setPhoneErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[index];
            // Shift errors down for subsequent indices
            const shiftedErrors: Record<number, string> = {};
            Object.entries(newErrors).forEach(([key, val]) => {
                const k = parseInt(key);
                if (k > index) shiftedErrors[k - 1] = val;
                else shiftedErrors[k] = val;
            });
            return shiftedErrors;
        });
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        // Validate phone numbers before submission
        const hasPhoneErrors = Object.keys(phoneErrors).length > 0;
        if (hasPhoneErrors) {
            toast.error('Please fix the phone number format before saving');
            return;
        }

        // Filter out empty phone numbers
        const cleanPhoneNumbers = (formData.phoneNumbers || []).filter(p => p && p.trim() !== '');
        setFormData(prev => ({ ...prev, phoneNumbers: cleanPhoneNumbers }));

        // Filter out read-only fields that shouldn't be sent to the API
        const {
            id,
            ownerId,
            branches,
            _count,
            owner,
            qrCode,
            tier,
            address, // Remove physical address field
            gstin, // Owned by the GSTIN and Address section
            registeredAddress, // Owned by the GSTIN and Address section
            defaultGstPercent, // Owned by the GSTIN and Address section
            includeGstinInInvoices, // Owned by the GSTIN and Address section
            ...updateData
        } = formData as any;

        updateMutation.mutate(updateData);
    };

    const sections = [
        { id: 'general', name: 'General', icon: Building2 },
        { id: 'business', name: 'GSTIN and Address', icon: ShieldCheck },
        { id: 'policy', name: 'Policy & Terms', icon: FileText },
        { id: 'notifications', name: 'Notifications', icon: Bell },
        { id: 'social', name: 'Social Media', icon: Globe },
        { id: 'team', name: 'Team & Trainers', icon: Award },
        { id: 'permissions', name: 'HRMS Permissions', icon: Lock },
        { id: 'team-permissions', name: 'Team Permissions', icon: Users },
        ...(paymentSettings?.enabled ? [{ id: 'payments', name: 'Payments', icon: CreditCard }] : []),
    ];

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
        );
    }

    return (
        <div className="max-w-6xl mx-auto space-y-8">
            {/* Premium Header */}
            <div className="bg-white p-8 rounded-[2.5rem] border border-gray-100 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-6 relative overflow-hidden group">
                <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-50/50 rounded-full blur-3xl -mr-32 -mt-32 transition-colors group-hover:bg-indigo-100/50"></div>
                <div className="relative">
                    <h1 className="text-3xl font-black text-gray-900 tracking-tight leading-none uppercase">Gym Settings</h1>
                    <p className="text-[10px] font-black text-gray-400 mt-2 uppercase tracking-[0.2em]">Configuration & Branding</p>
                </div>
            </div>

            <div className="flex flex-col md:flex-row gap-8">
                {/* Sidebar Navigation */}
                <aside className="w-full md:w-64">
                    {/* Mobile: Select Dropdown */}
                    <div className="md:hidden pb-4">
                        <select
                            value={activeSection}
                            onChange={(e) => setActiveSection(e.target.value)}
                            className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all outline-none text-sm font-medium text-gray-700 shadow-sm"
                        >
                            {sections.map((section) => (
                                <option key={section.id} value={section.id}>
                                    {section.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Desktop: Vertical List */}
                    <div className="hidden md:flex flex-col gap-1">
                        {sections.map((section) => (
                            <button
                                key={section.id}
                                onClick={() => setActiveSection(section.id)}
                                className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 w-full text-left ${activeSection === section.id
                                    ? 'bg-blue-50 text-blue-700 shadow-sm border border-blue-100'
                                    : 'text-gray-600 hover:bg-gray-50 border border-transparent'
                                    }`}
                            >
                                <section.icon className={`h-5 w-5 ${activeSection === section.id ? 'text-blue-600' : 'text-gray-400'}`} />
                                <span className="whitespace-nowrap">{section.name}</span>
                            </button>
                        ))}
                    </div>
                </aside>

                {/* Main Content Area */}
                <main className="flex-1 bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden min-w-0">
                    {activeSection === 'permissions' ? (
                        <HrmsPermissionsSection gymId={gymId || ''} />
                    ) : null}
                    {activeSection === 'team-permissions' ? (
                        <TeamPermissionsSection gymId={gymId || ''} />
                    ) : null}
                    {activeSection === 'business' ? (
                        <GstinAndAddressSection gymId={gymId || ''} />
                    ) : null}
                    {activeSection === 'payments' && paymentSettings ? (
                        <PaymentSettingsSection gymId={gymId || ''} settings={paymentSettings} onChange={() => refetchPaymentSettings()} />
                    ) : null}
                    <form onSubmit={handleSubmit} className={`divide-y divide-gray-100 ${activeSection === 'permissions' || activeSection === 'team-permissions' || activeSection === 'business' || activeSection === 'payments' ? 'hidden' : ''}`}>
                        {/* General Section */}
                        {activeSection === 'general' && (
                            <div className="p-4 sm:p-8 space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
                                <div>
                                    <h2 className="text-xl font-bold text-gray-900">General Settings</h2>
                                    <p className="text-sm text-gray-500 mt-1">Basic information about your gym</p>
                                </div>

                                {!formData.gymCode && (
                                    <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 flex gap-3">
                                        <Info className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                                        <div className="flex-1">
                                            <p className="text-sm font-medium text-blue-900">Complete Your Setup</p>
                                            <p className="text-sm text-blue-700 mt-1">
                                                Add your Gym Code below to enable automatic invoice and payslip generation with unique IDs.
                                            </p>
                                        </div>
                                    </div>
                                )}

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-2">
                                        <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                                            Gym Code <span className="text-red-500">*</span>
                                            <span title="Required for generating invoices and payslips.">
                                                <Info className="h-4 w-4 text-gray-400" />
                                            </span>
                                        </label>
                                        <input
                                            type="text"
                                            name="gymCode"
                                            value={formData.gymCode || ''}
                                            onChange={handleChange}
                                            className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all outline-none font-mono uppercase"
                                            maxLength={50}
                                            required
                                        />
                                        <p className="text-xs text-gray-500">
                                            Used in invoice/payslip IDs. Suggested: <span className="font-mono font-medium">{formData.name
                                                ? formData.name.replace(/[^a-zA-Z0-9]/g, '').substring(0, 5).toUpperCase()
                                                : 'IRONP'}</span>
                                        </p>
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                                            Gym Name <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="name"
                                            value={formData.name || ''}
                                            onChange={handleChange}
                                            className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all outline-none"
                                            placeholder="e.g. Iron Paradise Gym"
                                            required
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-sm font-semibold text-gray-700">Display Name</label>
                                        <input
                                            type="text"
                                            name="displayName"
                                            value={formData.displayName || ''}
                                            onChange={handleChange}
                                            className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all outline-none"
                                            placeholder="e.g. Iron Paradise"
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                                            <Mail className="h-4 w-4 text-gray-400" /> Email Address <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="email"
                                            name="email"
                                            value={formData.email || ''}
                                            onChange={handleChange}
                                            className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all outline-none"
                                            placeholder="contact@gym.com"
                                            required
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                                            <Globe className="h-4 w-4 text-gray-400" /> Website
                                        </label>
                                        <input
                                            type="text"
                                            name="website"
                                            value={formData.website || ''}
                                            onChange={handleChange}
                                            className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all outline-none"
                                            placeholder="e.g. www.gympe.com or gympe.com"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                                        <Phone className="h-4 w-4 text-gray-400" /> Phone Numbers
                                    </label>
                                    <div className="space-y-3">
                                        {formData.phoneNumbers?.map((phone, index) => (
                                            <div key={index} className="space-y-1">
                                                <div className="flex gap-2">
                                                    <input
                                                        type="text"
                                                        value={phone}
                                                        onChange={(e) => handlePhoneChange(index, e.target.value)}
                                                        className={`flex-1 px-4 py-2.5 bg-gray-50 border rounded-xl focus:ring-2 focus:bg-white transition-all outline-none ${phoneErrors[index] ? 'border-red-300 focus:ring-red-500' : 'border-gray-200 focus:ring-blue-500'
                                                            }`}
                                                        placeholder="98765 43210"
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => removePhone(index)}
                                                        className="p-2.5 text-red-500 hover:bg-red-50 rounded-xl transition-colors"
                                                    >
                                                        Remove
                                                    </button>
                                                </div>
                                                {phoneErrors[index] && (
                                                    <p className="text-xs text-red-600 ml-1">{phoneErrors[index]}</p>
                                                )}
                                            </div>
                                        ))}
                                        <button
                                            type="button"
                                            onClick={addPhone}
                                            className="text-sm font-medium text-blue-600 hover:text-blue-700 flex items-center gap-1"
                                        >
                                            + Add Phone Number
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Policy Section */}
                        {activeSection === 'policy' && (
                            <div className="p-4 sm:p-8 space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
                                <div>
                                    <h2 className="text-xl font-bold text-gray-900">Policy & Terms</h2>
                                    <p className="text-sm text-gray-500 mt-1">Customize your Terms & Conditions and Privacy Policy</p>
                                </div>

                                <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4 flex gap-3">
                                    <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                                    <p className="text-sm text-blue-800">
                                        If left blank, the default GymPe Terms and Privacy Policy will be used for your members.
                                        You can customize them here to reflect your gym's specific rules.
                                    </p>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-sm font-semibold text-gray-700">Terms & Conditions</label>
                                    <textarea
                                        name="termsAndConditions"
                                        value={formData.termsAndConditions || ''}
                                        onChange={handleChange}
                                        rows={10}
                                        className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all outline-none resize-none font-mono text-sm"
                                        placeholder="Enter your specific Terms & Conditions here..."
                                    />
                                </div>

                                <div className="space-y-2">
                                    <label className="text-sm font-semibold text-gray-700">Privacy Policy</label>
                                    <textarea
                                        name="privacyPolicy"
                                        value={formData.privacyPolicy || ''}
                                        onChange={handleChange}
                                        rows={10}
                                        className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all outline-none resize-none font-mono text-sm"
                                        placeholder="Enter your specific Privacy Policy here..."
                                    />
                                </div>
                            </div>
                        )}

                        {/* Notifications Section */}
                        {activeSection === 'notifications' && (
                            <div className="p-4 sm:p-8 space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
                                <div>
                                    <h2 className="text-xl font-bold text-gray-900">Notification Settings</h2>
                                    <p className="text-sm text-gray-500 mt-1">How your gym appears in member notifications</p>
                                </div>
                                <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4 flex gap-3">
                                    <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                                    <p className="text-sm text-blue-800">
                                        The notification name is used as the sender name in WhatsApp and Email notifications.
                                        If left blank, the Gym Name will be used.
                                    </p>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                                        <Bell className="h-4 w-4 text-gray-400" /> Name for Notifications
                                    </label>
                                    <input
                                        type="text"
                                        name="notificationName"
                                        value={formData.notificationName || ''}
                                        onChange={handleChange}
                                        className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all outline-none"
                                        placeholder="e.g. Iron Paradise Team"
                                    />
                                </div>

                                <div className="pt-6 border-t border-gray-100">
                                    <WhatsappSettings gymId={gymId || ''} />
                                </div>
                            </div>
                        )}

                        {/* Social Media Section */}
                        {activeSection === 'social' && (
                            <div className="p-4 sm:p-8 space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
                                <div>
                                    <h2 className="text-xl font-bold text-gray-900">Social Media</h2>
                                    <p className="text-sm text-gray-500 mt-1">Connect your social profiles</p>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-2">
                                        <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                                            <Instagram className="h-4 w-4 text-pink-600" /> Instagram Handle
                                        </label>
                                        <div className="relative">
                                            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">@</span>
                                            <input
                                                type="text"
                                                name="instagramHandle"
                                                value={formData.instagramHandle || ''}
                                                onChange={handleChange}
                                                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all outline-none"
                                                placeholder="username"
                                            />
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                                            <Youtube className="h-4 w-4 text-red-600" /> YouTube Channel
                                        </label>
                                        <input
                                            type="text"
                                            name="youtubeHandle"
                                            value={formData.youtubeHandle || ''}
                                            onChange={handleChange}
                                            className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all outline-none"
                                            placeholder="Channel ID or URL"
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                                            <Twitter className="h-4 w-4 text-blue-400" /> Twitter Handle
                                        </label>
                                        <div className="relative">
                                            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">@</span>
                                            <input
                                                type="text"
                                                name="twitterHandle"
                                                value={formData.twitterHandle || ''}
                                                onChange={handleChange}
                                                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all outline-none"
                                                placeholder="username"
                                            />
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                                            <Facebook className="h-4 w-4 text-blue-700" /> Facebook Page
                                        </label>
                                        <input
                                            type="text"
                                            name="facebookHandle"
                                            value={formData.facebookHandle || ''}
                                            onChange={handleChange}
                                            className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all outline-none"
                                            placeholder="Page URL or name"
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                                            <Linkedin className="h-4 w-4 text-blue-800" /> LinkedIn Profile
                                        </label>
                                        <input
                                            type="text"
                                            name="linkedinHandle"
                                            value={formData.linkedinHandle || ''}
                                            onChange={handleChange}
                                            className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all outline-none"
                                            placeholder="Company profile URL"
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Team & Trainers Section */}
                        {activeSection === 'team' && (
                            <div className="p-4 sm:p-8 space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
                                <div>
                                    <h2 className="text-xl font-bold text-gray-900">Team & Trainers</h2>
                                    <p className="text-sm text-gray-500 mt-1">Settings that affect trainer reporting and incentive calculation</p>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                                        <Award className="h-4 w-4 text-gray-400" /> Trainer Incentive Threshold
                                    </label>
                                    <div className="flex items-center gap-3">
                                        <NumberInput
                                            name="trainerIncentiveThreshold"
                                            integer
                                            min={0}
                                            max={100}
                                            step={1}
                                            emptyValue={50}
                                            value={formData.trainerIncentiveThreshold ?? 50}
                                            onChange={(v) => setFormData(prev => ({ ...prev, trainerIncentiveThreshold: v ?? 50 }))}
                                            className="w-28 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all outline-none"
                                            placeholder="50"
                                        />
                                        <span className="text-sm font-bold text-gray-500">%</span>
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">
                                        Members with an engagement of at least this % during the report period are counted as eligible for trainer incentive. Default 50%.
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Footer / Save Button */}
                        <div className="p-6 pb-20 sm:pb-6 bg-gray-50 flex justify-end safe-area-inset-bottom">
                            <button
                                type="submit"
                                disabled={updateMutation.isLoading || !hasChanges() || Object.keys(phoneErrors).length > 0}
                                className="flex items-center gap-2 px-8 py-3 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 transition-all shadow-lg shadow-blue-200 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {updateMutation.isLoading ? (
                                    <Loader2 className="h-5 w-5 animate-spin" />
                                ) : (
                                    <Save className="h-5 w-5" />
                                )}
                                Save Changes
                            </button>
                        </div>
                    </form>
                </main>
            </div>
        </div>
    );
}
